/**
 * Permissions selectors based on redux-json-api state.
 *
 * These are useful for various components which need to evaluate the
 * permissions of the currently logged in user.
 */
import { includes, filter, find, map, path, pathOr } from 'ramda';
import { createSelector } from 'reselect';

import { selectUserId } from './session';


/**
 * Basic redux state selectors.
 */
export const selectApiRoles = state => path(['api', 'api-role', 'data'], state) || [];
export const selectApiUsers = state => path(['api', 'api-user', 'data'], state) || [];
export const selectCompanies = state => path(['api', 'company', 'data'], state) || [];

/**
 * Selects a current `default_company` value from the `initialValues` prop of
 * the`ApiUserForm` component, if it exists.
 *
 * @param {Object} state Redux state object.
 * @param {Object} props `ApiUserForm` props.
 *
 * @returns {String | undefined} `default_company` id or undefined if not set
 * (e.g. when creating a new user).
 */
const selectDefaultCompanyIdFromInitialValues = (state, props) => path(['initialValues', 'default_company'], props);
const selectCompanyIdFromInitialValues = (state, props) => path(['initialValues', 'company'], props);

/**
 * Composed selectors that memoize their results.
 */

/**
 * Returns the currently logged in user's ApiUser-object.
 *
 * @param {String|Number} userId The user identifier of the currently logged in
 * user.
 *
 * @param {[ApiUser]} apiUsers All ApiUser objects currently present in Redux
 * state.
 *
 * @returns {ApiUser} The ApiUser object matching the user identifier of the
 * currently logged in user.
 */
export const getUser = createSelector([selectUserId, selectApiUsers], (userId, apiUsers) => {
  if (typeof userId !== 'number' && typeof userId !== 'string') return undefined;

  const isApiUser = apiUser => (Number(apiUser.id) === Number(userId));

  return find(isApiUser)(apiUsers);
});

/**
 * Returns the currently logged in user's ApiRole objects.
 *
 * @param {String|Number} userId The user identifier of the currently logged in
 * user.
 *
 * @param {[ApiRole]} apiRoles All ApiRole objects currently present in Redux
 * state.
 *
 * @returns {[ApiRole]} The ApiRole objects matching the user identifier of the
 * currently logged in user.
 */
export const getUserRoles = createSelector([selectUserId, selectApiRoles], (userId, apiRoles) => {
  if (typeof userId !== 'number' && typeof userId !== 'string') return [];

  const isApiRoleForUser = apiRole => {
    const apiRoleUserId = path(['relationships', 'user', 'data', 'id'], apiRole);

    return Number(apiRoleUserId) === Number(userId);
  };

  return filter(isApiRoleForUser)(apiRoles);
});

/**
 * Returns whether the currently logged in user has administrator privileges.
 *
 * @param {ApiUser} user The ApiUser object of the currently logged in user.
 *
 * @param {[ApiRole]} userRoles The ApiRole objects of the currently logged in
 * user.
 *
 * @returns {Boolean} Whether the currently logged in user has administrator
 * privileges or not.
 */
export const isAdmin = createSelector([getUser, getUserRoles], (user, userRoles) => {
  if (!user) return false;

  if (path(['attributes', 'is-superuser'], user) === true) return true;

  const isAdminRole = role => {
    if (path(['attributes', 'role'], role) === 'ADMIN') return true;

    return false;
  };

  const adminRole = find(isAdminRole)(userRoles);

  if (adminRole) return true;

  return false;
});

export const isSubcontractor = createSelector([getUser], (user) => (
  (path(['relationships', 'as-subcontractor', 'data'], user) || []).length > 0
));
export const isASL = createSelector([getUser], (user) => (
  (path(['relationships', 'as-asl', 'data'], user) || []).length > 0
));

export const isSubcontractorForUser = createSelector(
  [getUser, selectDefaultCompanyIdFromInitialValues],
  (user, userCompany) =>
    pathOr([], ['relationships', 'as-subcontractor', 'data'], user).map(c => c.id).includes(userCompany));

export const isSubcontractorForVehicle = createSelector(
  [getUser, selectCompanyIdFromInitialValues], (user, companyId) =>
    pathOr([], ['relationships', 'as-subcontractor', 'data'], user).map(c => c.id).includes(companyId));
/**
 * Returns a list of companies for the default_company field of a user. Used in
 * the ApiUserForm component.
 *
 * @param {Object} state Redux state object.
 * @param {Object} props `ApiUserForm` props.
 *
 * @returns {[Object]} A list of all companies available as `default_company`.
 */
export const getDefaultCompanies = createSelector(
  [getUser, selectCompanies, selectDefaultCompanyIdFromInitialValues],
  (user, companies, defaultCompanyId) => {
    // If the user is not valid, return an empty array.
    if (!user) return [];

    // If the user is an administrator, return all companies.
    if (path(['attributes', 'is-superuser'], user) === true ||
        (path(['relationships', 'as-administrator', 'data'], user) || []).length > 0)
      return companies;

    const subcontractorCompanyIds = (path(['relationships', 'as-subcontractor', 'data'], user) || []).map(c => Number(c.id));

    if (defaultCompanyId && !includes(subcontractorCompanyIds, Number(defaultCompanyId)))
      subcontractorCompanyIds.push(Number(defaultCompanyId));

    return filter(c => includes(Number(c.id), subcontractorCompanyIds), companies)
  },
);
